import React from 'react';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { useGet } from '../../hooks/useApi.js';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { formatHours, relativeTime } from '../../lib/format.js';
import { cn } from '@hts/ui';

/** GET /api/v1/schedule — ConnectWise schedule entries mirrored by the worker. */
export interface ScheduleWeek {
  weekStart: string;
  days: string[];
  hoursPerDay: number;
  lastSyncedAt: string | null;
  sourceStatus: "fresh" | "stale" | "unavailable";
  items: Array<{
    techId: string;
    name: string;
    role: string;
    avatarInitials: string;
    openTickets: number;
    days: Array<{ date: string; bookedHours: number; timeOffHours: number; blocks: number }>;
    weekBookedHours: number;
    capacityHours: number;
    utilization: number;
  }>;
}

/** Local Monday (YYYY-MM-DD) of the week containing `d`. */
function mondayOf(d: Date): string {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}

function addDays(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number];
  return mondayOf(new Date(y, m - 1, d + n));
}

function localDate(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

export function SchedulePage() {
  const thisWeek = mondayOf(new Date());
  const [weekStart, setWeekStart] = React.useState(thisWeek);
  const tz = new Date().getTimezoneOffset();
  const { data, loading, error, refetch } = useGet<ScheduleWeek>(`/api/v1/schedule?weekStart=${weekStart}&tz=${tz}`);
  const techs = data?.items ?? [];
  const days = data?.days ?? [];
  const todayKey = (() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  })();
  const hoursPerDay = data?.hoursPerDay ?? 8;
  const anyBookings = techs.some(t => t.days.some(d => d.blocks > 0));
  const rangeLabel = days.length
    ? `${localDate(days[0]!).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${localDate(days[days.length - 1]!).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    : '';

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-[hsl(var(--content))]">Scheduling</h1>
          <p className="text-sm text-[hsl(var(--content-subtle))] mt-0.5">
            Booked hours per technician from ConnectWise schedule entries
            {data?.lastSyncedAt ? ` · synced ${relativeTime(data.lastSyncedAt)}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2" role="group" aria-label="Week navigation">
          <button
            type="button"
            onClick={() => setWeekStart(w => addDays(w, -7))}
            aria-label="Previous week"
            className="rounded-lg border border-[hsl(var(--border))] p-2 text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] transition-colors"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setWeekStart(thisWeek)}
            disabled={weekStart === thisWeek}
            className="rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] transition-colors disabled:opacity-60 disabled:cursor-default"
          >
            {weekStart === thisWeek ? 'This week' : 'Back to this week'}
          </button>
          <button
            type="button"
            onClick={() => setWeekStart(w => addDays(w, 7))}
            aria-label="Next week"
            className="rounded-lg border border-[hsl(var(--border))] p-2 text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] transition-colors"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
          <span className="text-sm font-medium text-[hsl(var(--content))] min-w-[8rem] text-center">{rangeLabel}</span>
          <button
            type="button"
            onClick={() => refetch()}
            aria-label="Refresh"
            className="rounded-lg border border-[hsl(var(--border))] p-2 text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {error ? (
        <ErrorState message={`Could not load the schedule (${error}).`} onRetry={refetch} />
      ) : loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => <CardSkeleton key={i} lines={2} />)}
        </div>
      ) : techs.length === 0 ? (
        <EmptyState icon="📅" title="No technicians" description="Technicians appear after the ConnectWise member sync. Add them under People › Technicians." />
      ) : (
        <>
          {!anyBookings && (
            <p className="rounded-lg border border-dashed border-[hsl(var(--border))] px-4 py-3 text-sm text-[hsl(var(--content-subtle))]">
              {data?.sourceStatus === 'stale'
                ? `Schedule data is stale (last synced ${data.lastSyncedAt ? relativeTime(data.lastSyncedAt) : 'at an unknown time'}). This empty week may not reflect ConnectWise.`
                : data?.sourceStatus === 'unavailable' || !data?.lastSyncedAt
                ? 'Schedule entries have never synced from ConnectWise. This is not a confirmed free week.'
                : 'ConnectWise synced successfully and returned zero schedule entries for these technicians this week.'}
            </p>
          )}
          <div className="overflow-x-auto rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-sm">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))]">
                  <th scope="col" className="py-3 px-4 text-left text-xs font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide w-40">Technician</th>
                  {days.map(d => (
                    <th scope="col" key={d} className="py-3 px-3 text-center text-xs font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide">
                      <div>{localDate(d).toLocaleDateString('en-US', { weekday: 'short' })}</div>
                      <div className={cn(
                        'text-base font-bold mt-0.5',
                        d === todayKey ? 'text-brand-600' : 'text-[hsl(var(--content))]'
                      )}>
                        {localDate(d).getDate()}
                      </div>
                    </th>
                  ))}
                  <th scope="col" className="py-3 px-3 text-center text-xs font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide">Open tickets</th>
                  <th scope="col" className="py-3 px-3 text-center text-xs font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide">Utilization</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--border))]">
                {techs.map(tech => (
                  <tr key={tech.techId} className="hover:bg-[hsl(var(--surface-subtle))] transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2.5">
                        <div className="h-7 w-7 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center text-xs font-bold text-brand-700 dark:text-brand-300 shrink-0">
                          {tech.avatarInitials}
                        </div>
                        <div>
                          <p className="font-medium text-[hsl(var(--content))] text-xs truncate max-w-[120px]">{tech.name}</p>
                          {tech.role && <p className="text-[10px] text-[hsl(var(--content-subtle))] truncate max-w-[120px]">{tech.role}</p>}
                        </div>
                      </div>
                    </td>
                    {tech.days.map(day => {
                      const isToday = day.date === todayKey;
                      const off = day.timeOffHours >= hoursPerDay;
                      const fill = Math.min((day.bookedHours + day.timeOffHours) / hoursPerDay, 1);
                      const label = data?.sourceStatus && data.sourceStatus !== "fresh"
                        ? "Unavailable"
                        : off
                        ? 'Off'
                        : day.bookedHours > 0
                        ? formatHours(day.bookedHours)
                        : day.timeOffHours > 0
                        ? `${formatHours(day.timeOffHours)} off`
                        : 'Free';
                      return (
                        <td key={day.date} className={cn('py-2 px-3 text-center', isToday && 'bg-brand-50/50 dark:bg-brand-900/5')}>
                          <div
                            className="mx-auto h-6 w-full max-w-[80px] rounded bg-[hsl(var(--surface-subtle))] relative overflow-hidden"
                            title={`${day.bookedHours}h booked${day.timeOffHours ? `, ${day.timeOffHours}h off` : ''} · ${day.blocks} entr${day.blocks === 1 ? 'y' : 'ies'}`}
                          >
                            <div
                              className={cn('absolute inset-y-0 left-0 rounded opacity-70', off ? 'bg-slate-400' : 'bg-brand-400')}
                              style={{ width: `${fill * 100}%` }}
                            />
                            <span className="relative z-10 text-[10px] font-medium text-[hsl(var(--content))] leading-6">{label}</span>
                          </div>
                        </td>
                      );
                    })}
                    <td className="py-3 px-3 text-center text-xs font-medium text-[hsl(var(--content))] tabular-nums">{tech.openTickets}</td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1.5" title={`${tech.weekBookedHours}h of ${tech.capacityHours}h`}>
                        <div className="h-1.5 w-16 rounded-full bg-[hsl(var(--surface-subtle))]">
                          <div
                            className={cn(
                              'h-full rounded-full',
                              tech.utilization >= 0.9 ? 'bg-red-500' :
                              tech.utilization >= 0.7 ? 'bg-amber-500' : 'bg-green-500'
                            )}
                            style={{ width: `${tech.utilization * 100}%` }}
                          />
                        </div>
                        <span className={cn(
                          'text-xs font-medium',
                          tech.utilization >= 0.9 ? 'text-red-600' :
                          tech.utilization >= 0.7 ? 'text-amber-600' : 'text-green-600'
                        )}>
                          {Math.round(tech.utilization * 100)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-[hsl(var(--content-subtle))] flex-wrap">
        <div className="flex items-center gap-1.5"><div className="h-2 w-2 rounded-full bg-green-500" /> Under 70% booked</div>
        <div className="flex items-center gap-1.5"><div className="h-2 w-2 rounded-full bg-amber-500" /> 70–90%</div>
        <div className="flex items-center gap-1.5"><div className="h-2 w-2 rounded-full bg-red-500" /> Over 90%</div>
        <div className="flex items-center gap-1.5"><div className="h-2 w-2 rounded-full bg-slate-400" /> Time off</div>
        <span>Capacity: {hoursPerDay}h/day minus time off</span>
      </div>
    </div>
  );
}
